<template>
  <div>
    <div v-if="hasPhotos" class="photos-container mb-4">
      <template v-for="(photo, index) in value">
        <component
          :is="photo.resized ? 'tooltip' : 'span'"
          :key="`tooltip-${index}`"
          :label="photo.resized ? $t('assets.resize-photo-tooltip' , { width: photo.width , height: photo.height }):''"
          :multilined="true"
          :disabled="true"
          position="is-right"
        >
          <DrawablePhotoV1OrV2
            v-if="isVisiblePhoto(photo)"
            :key="photo.uuid"
            photo-class="mr-0 mb-0"
            :image="photo"
            :drawing="photo.drawing"
            :thumbnail="photo.thumbnail"
            :width="photo.width"
            :height="photo.height"
            :loading="photo.loading"
            :allow-drawing="!disabled"
            :can-be-deleted="!disabled"
            small
            @change="updateDrawing(photo.uuid, arguments[0])"
            @delete="photoUuidToDelete = photo.uuid"
          />
        </component>
      </template>
    </div>

    <label class="btn btn-default text-primary px-0" :class="disabled ? 'opacity-50' : ''">
      <input
        ref="fileInput"
        class="hidden"
        type="file"
        :accept="allowedImageTypesInputTag"
        :disabled="loading || disabled"
        multiple
        @change="addPhoto"
      >
      <div :disabled="loading || disabled">
        <Loader v-if="loading" />
        <template v-else>
          <span class="cursor-pointer">+ {{ $t('Add photo') }}</span>
        </template>
      </div>
    </label>

    <DeleteResourceModal
      v-if="photoUuidToDelete && !disabled"
      mode="delete"
      @confirm="deletePhoto(photoUuidToDelete)"
      @close="photoUuidToDelete = false"
    >
      <div class="p-8">
        <Heading :level="2" class="mb-6">
          {{ $t('Delete Photo') }}
        </Heading>
        <p class="text-80 leading-normal">
          {{ $t('Are you sure you want to delete this photo?') }}
        </p>
      </div>
    </DeleteResourceModal>
  </div>
</template>

<script>
  import DeleteResourceModal from '@/_legacy/components/Modals/DeleteResourceModal.vue';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import Heading from '@/_legacy/components/Heading.vue';
  import { eventBus } from '@/_legacy/eventBus';
  import { DrawablePhotoV1OrV2 } from '@/_legacy/components/DrawablePhoto/DrawablePhotoV1OrV2';
  import PhotoReader from '@/_legacy/util/PhotoReader';
  import { AssetsPhotoActions, getPhotoStub } from '@/services/assets-api/AssetsPhotoService';
  import * as AssetsPhotoStateService from '@/services/assets-api/AssetsPhotoStateService';
  import { Tooltip } from '@/ui/components/Tooltip';
  import { allowedImageTypesInputTag } from '@/services/assets-api/assetsValidation';

  const photoStubDictionary = [];

  // Note: this one used for issues only
  export default {
    name: 'AddPhotos',
    components: {
      DrawablePhotoV1OrV2,
      Loader,
      DeleteResourceModal,
      Heading,
      Tooltip,
    },
    props: {
      value: {
        type: Array,
        required: true,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data: () => ({
      loading: false,
      photoUuidToDelete: false,
      visiblePhotos: [],
      allowedImageTypesInputTag,
    }),
    computed: {
      hasPhotos() {
        return this.visiblePhotos && this.visiblePhotos.length > 0;
      },
    },
    watch: {
      value: {
        handler(value) {
          if (!value) {
            return;
          }
          this.visiblePhotos = value.filter(photo => photo.action !== AssetsPhotoActions.DELETE_IMAGE);
          this.$forceUpdate();
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      isVisiblePhoto(photo) {
        return photo.action !== AssetsPhotoActions.DELETE_IMAGE;
      },
      deletePhoto(uuid) {
        this.loading = true;
        const photoToDelete = this.value.find(photo => photo.uuid === uuid);

        AssetsPhotoStateService.deletePhoto(photoToDelete);

        this.$emit('input', [...this.value]);
        this.photoUuidToDelete = false;
        this.loading = false;
      },
      removeDrawing(uuid) {
        const photoToDelete = this.value.find(photo => photo.uuid === uuid);

        AssetsPhotoStateService.deleteDrawing(photoToDelete);

        this.$emit('input', [...this.value]);
      },
      updateDrawing(uuid, drawing) {
        this.loading = true;

        if (!drawing) {
          this.removeDrawing(uuid);

          this.loading = false;
          return;
        }

        const photoToUpdate = this.value.find(photo => photo.uuid === uuid);

        AssetsPhotoStateService.updateDrawing(photoToUpdate, drawing);

        this.$emit('input', [...this.value]);
        this.loading = false;
      },
      processUploadError(photoStub, error) {
        const errorMessage = `${this.$t('Error loading image: ')} ${error}`;
        eventBus.$emit('error', errorMessage);

        const index = this.value.indexOf(photoStub);
        if (index !== -1) {
          this.value.splice(index, 1);
        }
      },
      async addPhoto(event) {
        if (!event.target.files) {
          return;
        }

        this.loading = true;

        // first iteration creates the loading placeholders
        const originalFiles = this.value;

        for (let i = 0; i < event.target.files.length; i++) {
          const photoStub = getPhotoStub();
          photoStubDictionary[i] = photoStub;
        }

        const processedFiles = [];

        //this iteration uploads all
        for (let i = 0; i < event.target.files.length; i++) {
          const file = event.target.files[i];
          const photoStub = photoStubDictionary[i];
          if (!photoStub || !file) {
            continue;
          }

          try {
            const resizedPhoto = await PhotoReader.read(file);

            photoStub.thumbnail = resizedPhoto.thumbnail;
            photoStub.width = resizedPhoto.width;
            photoStub.height = resizedPhoto.height;
            photoStub.resized = resizedPhoto.resized;
            AssetsPhotoStateService.addImage(photoStub, resizedPhoto.photo);

            this.$delete(photoStub, 'loading');

            processedFiles.push(photoStub);
          } catch (error) {
            this.processUploadError(photoStub, error);
          }
        }

        this.$emit('input', [...originalFiles, ...processedFiles]);
        this.$refs.fileInput.value = null;
        this.loading = false;
      },
    },
  };
</script>

<style scoped>
label {
  height: 23px;
  line-height: normal;
  padding: 0;
}

.photos-container {
  display: grid;
  width: fit-content;
  grid-gap: 1.5rem 1rem;
  padding-top: 0.375rem;
}

.photos-container > div {
  margin-right: 0;
  margin-bottom: 0;
}
</style>
